import React, { useState, useMemo } from "react";

/**
 * <MatchPairs /> — Cycle 13.4
 *
 * A tap-tap matching widget. Kid taps one item on the left, then one
 * on the right, and they get connected. Tapping a connected item
 * disconnects. Colour-codes pairs so the child can verify at a glance.
 *
 * Props:
 *   - pairs_left  : [{ iso3, name }]
 *   - pairs_right : [{ idx, capital }] (idx is the STABLE key referenced
 *                   in the server-side answer)
 *   - value       : { iso3 -> idx } (controlled)
 *   - onChange    : (dict) => void
 *   - testIdPrefix
 */
const PAIR_COLORS = [
  "bg-rose-200 border-rose-800",
  "bg-emerald-200 border-emerald-800",
  "bg-sky-200 border-sky-800",
  "bg-amber-200 border-amber-800",
  "bg-purple-200 border-purple-800",
  "bg-fuchsia-200 border-fuchsia-800",
];

export default function MatchPairs({ pairs_left = [], pairs_right = [], value, onChange, testIdPrefix = "mp" }) {
  const pairs = useMemo(() => (value && typeof value === "object" ? value : {}), [value]);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);

  const pairIndexFor = (iso3) => {
    // Colour index based on order of matches — stable per session.
    const keys = Object.keys(pairs);
    const i = keys.indexOf(iso3);
    return i >= 0 ? i % PAIR_COLORS.length : -1;
  };

  const colorForLeft = (iso3) => {
    const i = pairIndexFor(iso3);
    return i >= 0 ? PAIR_COLORS[i] : "bg-white border-slate-900";
  };
  const colorForRight = (idx) => {
    for (const [iso3, mIdx] of Object.entries(pairs)) {
      if (Number(mIdx) === Number(idx)) {
        const i = Object.keys(pairs).indexOf(iso3);
        return i >= 0 ? PAIR_COLORS[i % PAIR_COLORS.length] : "bg-white border-slate-900";
      }
    }
    return "bg-white border-slate-900";
  };

  const commit = (next) => onChange && onChange(next);

  const tapLeft = (iso3) => {
    // If already matched, tapping removes the match.
    if (pairs[iso3] !== undefined) {
      const next = { ...pairs };
      delete next[iso3];
      commit(next);
      setSelectedLeft(null);
      return;
    }
    setSelectedLeft((prev) => (prev === iso3 ? null : iso3));
    if (selectedRight !== null) {
      // Try to commit the pair — but only if the right isn't already used
      const nextRight = selectedRight;
      const usedBy = Object.entries(pairs).find(([, v]) => Number(v) === Number(nextRight))?.[0];
      const next = { ...pairs };
      if (usedBy) delete next[usedBy];
      next[iso3] = nextRight;
      commit(next);
      setSelectedLeft(null);
      setSelectedRight(null);
    }
  };

  const tapRight = (idx) => {
    // If already used, remove that pair
    const usedBy = Object.entries(pairs).find(([, v]) => Number(v) === Number(idx))?.[0];
    if (usedBy) {
      const next = { ...pairs };
      delete next[usedBy];
      commit(next);
      setSelectedRight(null);
      return;
    }
    setSelectedRight((prev) => (prev === idx ? null : idx));
    if (selectedLeft !== null) {
      const iso3 = selectedLeft;
      const next = { ...pairs };
      next[iso3] = idx;
      commit(next);
      setSelectedLeft(null);
      setSelectedRight(null);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`${testIdPrefix}-grid`}>
      <div className="space-y-1.5" data-testid={`${testIdPrefix}-left`}>
        {pairs_left.map((it) => (
          <button
            key={it.iso3}
            type="button"
            onClick={() => tapLeft(it.iso3)}
            data-testid={`${testIdPrefix}-left-${it.iso3}`}
            data-matched={pairs[it.iso3] !== undefined ? "true" : "false"}
            className={`w-full min-h-11 px-3 py-2 rounded-2xl border-[3px] font-heading font-bold text-sm text-left shadow-[0_3px_0_0_rgba(15,23,42,1)] active:translate-y-[1px] active:shadow-none ${
              selectedLeft === it.iso3 ? "ring-4 ring-yellow-400 " : ""
            }${colorForLeft(it.iso3)}`}
          >
            {it.name}
          </button>
        ))}
      </div>
      <div className="space-y-1.5" data-testid={`${testIdPrefix}-right`}>
        {pairs_right.map((it) => (
          <button
            key={it.idx}
            type="button"
            onClick={() => tapRight(it.idx)}
            data-testid={`${testIdPrefix}-right-${it.idx}`}
            className={`w-full min-h-11 px-3 py-2 rounded-2xl border-[3px] font-heading font-bold text-sm text-left shadow-[0_3px_0_0_rgba(15,23,42,1)] active:translate-y-[1px] active:shadow-none ${
              selectedRight === it.idx ? "ring-4 ring-yellow-400 " : ""
            }${colorForRight(it.idx)}`}
          >
            {it.capital}
          </button>
        ))}
      </div>
    </div>
  );
}
